import type {
  FormField,
  FormFieldValue,
} from '@/screens/ReportCreation/reportCreation.types';
import type { TFunction } from 'i18next';
import {
  formatBooleanValue,
  formatDateForLocale,
  getDiagnosticProcedureFieldValue,
  getReportFieldValue,
  resolveOptionLabel,
} from '../../reportDetail.utils';
import { ReportDetailFieldSource } from './reportDetailFieldRenderer.type';

export const getReportDetailFieldValue = (
  fieldId: string,
  source: ReportDetailFieldSource,
  language: string,
): FormFieldValue =>
  source.kind === 'report'
    ? getReportFieldValue(source.report, fieldId, language)
    : getDiagnosticProcedureFieldValue(source.procedure, fieldId, language);

interface FormatReportDetailFieldValueParams {
  field: FormField;
  rawValue: FormFieldValue;
  source: ReportDetailFieldSource;
  language: string;
  t: TFunction;
}

export const formatReportDetailFieldValue = ({
  field,
  rawValue,
  source,
  language,
  t,
}: FormatReportDetailFieldValueParams): string | number | undefined => {
  if (source.kind === 'report') {
    if (field.type === 'location') {
      const latitude = getReportFieldValue(
        source.report,
        'location.latitude',
        language,
      );
      const longitude = getReportFieldValue(
        source.report,
        'location.longitude',
        language,
      );

      if (!latitude || !longitude) {
        return undefined;
      }

      return `${latitude}, ${longitude}`;
    }

    switch (field.id) {
      case 'species': {
        const speciesLabel = resolveOptionLabel(
          field.type === 'select' ? field.options : [],
          rawValue,
        );
        const order = source.report.order;
        const combined = [speciesLabel, order].filter(Boolean).join(' / ');
        return combined || undefined;
      }
      case 'dateOfDetection':
        return formatDateForLocale(rawValue, language);
      case 'inaccurateLocation':
        return formatBooleanValue(rawValue, t);
      case 'humanAffected':
      case 'domesticAnimalAffected':
        return rawValue ? t(`reportDetail.values.${rawValue}`) : undefined;
      default:
        break;
    }
  }

  if (field.type === 'toggle-group' || field.type === 'select') {
    return resolveOptionLabel(field.options, rawValue);
  }

  if (typeof rawValue === 'string' || typeof rawValue === 'number') {
    return rawValue;
  }

  return undefined;
};
